<script setup lang="ts">
import {computed, ref, watch} from "vue";
import {useRoute} from "vue-router";
import {History, Minus, Settings2, Sparkles, X} from "@lucide/vue";
import {storeToRefs} from "pinia";
import {Tooltip, TooltipContent, TooltipProvider, TooltipTrigger} from "@/core/components/ui/tooltip";
import {useAiAssistantStore} from "@/stores/aiAssistant.store";
import useAssistantChat from "@/ai-assistant/composables/useAssistantChat";
import {ASSISTANT_ROUTES} from "@/ai-assistant/constants";
import AssistantComposer from "@/ai-assistant/components/AssistantComposer.vue";
import AssistantConversation from "@/ai-assistant/components/AssistantConversation.vue";
import AssistantHistoryPanel from "@/ai-assistant/components/AssistantHistoryPanel.vue";
import AssistantSettingsPanel from "@/ai-assistant/components/AssistantSettingsPanel.vue";
import {Proposition} from "@/ai-assistant/interfaces";

const store = useAiAssistantStore();
const {isOpen, minimized, isStreaming, settingsExpanded, historyExpanded, assistantKey} = storeToRefs(store);
const {ask, stop} = useAssistantChat();
const route = useRoute();

/**
 * Hidden and not unmounted outside the calendar: an answer still being written
 * finishes, and the conversation is whole on the way back.
 */
const available = computed<boolean>(() => ASSISTANT_ROUTES.includes(String(route.name)));

/**
 * An answer finished while the window was folded down, and not yet seen.
 *
 * The pill says so: the pharmacist folded the window to keep working, and is
 * told when it is worth unfolding rather than having to check.
 */
const answered = ref(false);

watch(isStreaming, (streaming, wasStreaming) => {
    // A failed answer is not one worth unfolding for: the error banner waits
    // in the window.
    if (wasStreaming && !streaming && minimized.value && !store.error) {
        answered.value = true;
    }
});

watch(minimized, (folded) => {
    if (!folded) {
        answered.value = false;
    }
});

/**
 * Put the scenario back in the composer, without sending anything.
 *
 * Asking on the pharmacist's behalf would be one more thing happening without
 * their click. The sentence is opened, not finished: they say what to adjust.
 */
const adjust = (proposition: Proposition) => {
    const origin = proposition.author?.origin;
    const scenario = typeof origin === 'string' && origin ? `« ${origin} »` : 'ce scénario';

    store.setDraft(`À propos de ${scenario} : `);
};
</script>

<template>
  <!--
    A window floating over the screen rather than a column beside it: on a
    counter screen a column took a third of the width from a grid that has
    none to spare, and below that it covered the grid outright. Floating, it
    covers one corner, and folds down to a pill when that corner is needed.

    Deliberately not modal: the pharmacist keeps reading and scrolling the
    planning while the assistant answers about it.

    The HubSpot support launcher is anchored to the viewport's bottom right
    corner by a third party script and cannot be moved: the window and the
    pill stand above and beside it.

    Hidden rather than unmounted, folded down included: closing the window must
    not cut an answer being written, and reopening must find it whole.
  -->
  <aside
    v-show="isOpen && !minimized && available"
    class="fixed bottom-24 right-6 z-30 flex h-[min(36rem,calc(100vh_-_11rem))] w-[22.5rem] max-w-[calc(100vw_-_3rem)] flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-deep"
    aria-label="Assistant planning d'équipe"
  >
    <header class="flex shrink-0 items-start gap-1.5 border-b border-primary-lightest py-3 pl-4 pr-2.5">
      <div class="mr-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-r from-primary-dark to-primary">
        <Sparkles class="size-4 text-white" />
      </div>
      <div class="min-w-0 flex-1">
        <p class="truncate text-sm font-semibold text-primary">
          Assistant planning d'équipe
        </p>
        <p class="mt-0.5 flex items-center gap-1.5 text-[11px] text-gray-dark/70">
          <span class="size-1.5 shrink-0 rounded-full bg-green" />
          <span class="truncate">Copilote pour gérer le planning de votre pharmacie</span>
        </p>
      </div>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger as-child>
            <button
              class="flex size-7 shrink-0 items-center justify-center rounded-lg text-gray-dark/60 transition-colors hover:bg-primary-lightest hover:text-primary"
              @click="store.toggleHistory()"
            >
              <History class="size-4" />
            </button>
          </TooltipTrigger>
          <TooltipContent>Historique des propositions</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger as-child>
            <button
              class="flex size-7 shrink-0 items-center justify-center rounded-lg text-gray-dark/60 transition-colors hover:bg-primary-lightest hover:text-primary"
              @click="store.toggleSettings()"
            >
              <Settings2 class="size-4" />
            </button>
          </TooltipTrigger>
          <TooltipContent>Paramètres du copilote</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger as-child>
            <button
              class="flex size-7 shrink-0 items-center justify-center rounded-lg text-gray-dark/60 transition-colors hover:bg-primary-lightest hover:text-primary"
              aria-label="Réduire"
              @click="store.minimize()"
            >
              <Minus class="size-4" />
            </button>
          </TooltipTrigger>
          <TooltipContent>Réduire, la conversation continue</TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <button
        class="flex size-7 shrink-0 items-center justify-center rounded-lg text-gray-dark/60 transition-colors hover:bg-primary-lightest hover:text-primary"
        title="Fermer"
        @click="store.close()"
      >
        <X class="size-4" />
      </button>
    </header>

    <AssistantSettingsPanel v-if="settingsExpanded" :assistant-key="assistantKey" />

    <!-- Mounted on demand: it reads the pharmacy's propositions when it opens. -->
    <AssistantHistoryPanel v-if="historyExpanded" />

    <AssistantConversation @adjust="adjust" />

    <div class="shrink-0 border-t border-primary-lightest px-4 py-3">
      <AssistantComposer
        :is-streaming="isStreaming"
        @send="ask($event)"
        @stop="stop()"
      />
    </div>
  </aside>

  <button
    v-if="isOpen && minimized && available"
    class="fixed bottom-6 right-24 z-30 flex h-12 max-w-[calc(100vw_-_8rem)] items-center gap-2.5 rounded-full border border-solid border-gray-200 bg-white py-0 pl-2 pr-4 text-[13px] font-medium text-gray-dark shadow-deep transition-colors hover:border-primary/40"
    aria-label="Rouvrir l'assistant planning d'équipe"
    @click="store.open(assistantKey)"
  >
    <span class="flex size-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-r from-primary-dark to-primary">
      <Sparkles class="size-4 text-white" />
    </span>
    <span class="truncate">Assistant planning</span>
    <span v-if="isStreaming" class="shrink-0 text-[11px] font-normal text-gray-500">
      réponse en cours…
    </span>
    <span v-else-if="answered" class="flex shrink-0 items-center gap-1.5 text-[11px] font-normal text-gray-500">
      <span class="size-1.5 rounded-full bg-green" />
      réponse prête
    </span>
  </button>
</template>
